"use client"

import { useDictionary } from "@/core/hooks/use-dictionary";
import { Control, FieldErrors, useFieldArray, UseFormRegister, Controller } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput } from "../GlobalWidgetEditor";
import { Heading } from "@/components/ui/Heading";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface SpecGridWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const SpecGridWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: SpecGridWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            image: undefined,
            title: '',
            title_en: '',
            title_ar: '',
        });
    };

    const removeItem = (itemIndex: number) => {
        if (fields.length <= 1) return;
        remove(itemIndex);
    };

    return (
        <>
            {/* عنوان و توضیح کلیِ بخش (اختیاری)، مثلاً «ویژگی‌های فنی» */}
            <MultilingualInput
                baseName={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            <MultilingualTextareaInput
                baseName={`${name}.${widgetIndex}.description`}
                label={dictionary.forms?.description}
                register={register}
                errors={errors}
                disabled={disabled}
            />

            {fields.map((field, itemIndex) => {
                const imageError = getError(`${name}.${widgetIndex}.items.${itemIndex}.image`) as string | undefined;

                return (
                    <div key={field.id} className="border p-4 rounded-md space-y-4">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                آیتم {itemIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(itemIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        <MultilingualInput
                            baseName={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                            label={dictionary.forms?.title}
                            register={register}
                            errors={errors}
                            disabled={disabled}
                        />

                        <div className="space-y-1">
                            <label className="text-xs text-primary-600">
                                تصویر مشخصات فنی (ترجیحاً پس‌زمینه‌ی سفید/شفاف)
                            </label>
                            <Controller
                                control={control}
                                name={`${name}.${widgetIndex}.items.${itemIndex}.image`}
                                render={({ field: imageField }) => (
                                    <FileUploadInput
                                        accept="image/*"
                                        disabled={disabled}
                                        onUploadSuccess={(image) => {
                                            if (!Array.isArray(image) && image) imageField.onChange(image);
                                        }}
                                        defaultImages={
                                            imageField.value
                                                ? [
                                                    {
                                                        ...imageField.value,
                                                        name: imageField.value.name || '',
                                                        description: imageField.value.description || '',
                                                    },
                                                ]
                                                : []
                                        }
                                        onDefaultRemove={() => imageField.onChange(undefined)}
                                        error={imageError}
                                    />
                                )}
                            />
                        </div>
                    </div>
                );
            })}

            <Button type="button" variant="outline" onClick={addItem} disabled={disabled} className="mt-2">
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}
            </Button>
        </>
    );
};